import React, { useEffect, useState } from 'react'
import { Box, FormControl, InputLabel, MenuItem, Paper, Select, Stack, TextField, Typography } from '@mui/material'
import {
  AlertDialog,
  EditActions,
  ResponsibilityMultiSelect,
  useGlobalMessageHandler,
  useUsRoleTypeLuts
} from 'components'
import {
  type ExecutiveOrganizationBasicInfo,
  updateExecutiveOrganization
} from 'services'
import { FEDERAL_EXECUTIVE_ROLES, STATE_EXECUTIVE_ROLES, isStateExecutiveRole } from 'utils'
import { formatErrorMessages } from 'interfaces'

interface Props {
  orgId: number
  name: string
  code: string | null
  roleId: number
  responsibilities: number[]
  canEdit: boolean
  onUpdated: (updated: ExecutiveOrganizationBasicInfo) => void
  onRoleChanged?: () => void
}

export const ExecutiveOrgDetailsCard: React.FC<Props> = ({
  orgId,
  name: savedName,
  code: savedCode,
  roleId: savedRoleId,
  responsibilities: savedResponsibilities,
  canEdit,
  onUpdated,
  onRoleChanged
}) => {
  const { showErrorMessage, showSuccessMessage, clearMessages } = useGlobalMessageHandler()
  const { lutValues: roleLut } = useUsRoleTypeLuts()
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [name, setName] = useState(savedName)
  const [code, setCode] = useState(savedCode ?? '')
  const [roleId, setRoleId] = useState(savedRoleId)
  const [responsibilities, setResponsibilities] = useState<number[]>(savedResponsibilities)

  // Re-seed the fields from the saved snapshot whenever it changes — e.g. the
  // page refetched the org, or a save returned server-normalized values back
  // through props via onUpdated — but never clobber an in-progress edit.
  useEffect(() => {
    if (editing) return
    setName(savedName)
    setCode(savedCode ?? '')
    setRoleId(savedRoleId)
    setResponsibilities(savedResponsibilities)
  }, [editing, savedName, savedCode, savedRoleId, savedResponsibilities])

  const branchRoleIds = new Set(
    isStateExecutiveRole(savedRoleId) ? STATE_EXECUTIVE_ROLES : FEDERAL_EXECUTIVE_ROLES
  )
  const roleOptions = Array.from(roleLut.roles.values())
    .filter(role => branchRoleIds.has(role.id))
    .sort((a, b) => a.desc.localeCompare(b.desc))
  const roleChanged = roleId !== savedRoleId

  const handleCancel = (): void => {
    clearMessages()
    setName(savedName)
    setCode(savedCode ?? '')
    setRoleId(savedRoleId)
    setResponsibilities(savedResponsibilities)
    setEditing(false)
  }

  const handleSave = (): void => {
    if (name.trim() === '') {
      showErrorMessage('Name is required.')
      return
    }
    clearMessages()
    if (roleChanged) {
      setConfirmOpen(true)
      return
    }
    void performSave()
  }

  const performSave = async (): Promise<void> => {
    setSaving(true)
    try {
      const response = await updateExecutiveOrganization(orgId, {
        name: name.trim(),
        code: code.trim() === '' ? null : code.trim(),
        responsibilities,
        role: { id: roleId }
      })
      if (!response.succeeded || response.result == null) {
        const message = formatErrorMessages(response.errors)
        showErrorMessage(message !== '' ? message : (response.message ?? 'Failed to update organization.'))
        return
      }
      showSuccessMessage('Organization updated.')
      onUpdated(response.result)
      if (roleChanged) onRoleChanged?.()
      setEditing(false)
    } catch {
      showErrorMessage('Failed to update organization.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant='h6'>Details</Typography>
        <TextField label='Name' value={name} disabled={!editing} onChange={event => { setName(event.target.value) }} inputProps={{ maxLength: 150 }} fullWidth />
        <TextField label='Code' value={code} disabled={!editing} onChange={event => { setCode(event.target.value) }} inputProps={{ maxLength: 10 }} fullWidth />
        <FormControl fullWidth disabled={!editing}>
          <InputLabel>Role Type</InputLabel>
          <Select label='Role Type' value={roleOptions.length > 0 ? roleId : ''} onChange={event => { setRoleId(Number(event.target.value)) }}>
            {roleOptions.map(role => (
              <MenuItem key={role.id} value={role.id}>{role.desc}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box>
          <Typography variant='subtitle2' sx={{ mb: 1 }}>Responsibilities</Typography>
          <ResponsibilityMultiSelect value={responsibilities} onChange={setResponsibilities} disabled={!editing} />
        </Box>
        {canEdit && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <EditActions
              entityName='Organization'
              editing={editing}
              saving={saving}
              savePlacement='left'
              onEdit={() => { setEditing(true) }}
              onCancel={handleCancel}
              onSave={handleSave}
            />
          </Box>
        )}
      </Stack>
      <AlertDialog
        open={confirmOpen}
        title='Change role type?'
        contentText={
          <>Changing the role to <strong>{roleLut.roles.get(roleId)?.desc ?? ''}</strong> also re-types every
          sub-organization beneath it. Every position in this organization and its sub-organizations, including
          vacancies, must already have <strong>{roleLut.roles.get(roleId)?.desc ?? ''}</strong> as its Branch
          Role — change each position on its own page first.</>
        }
        nameButtonConfirm='Change Role'
        onButtonClick={() => { void performSave() }}
        onClose={() => { setConfirmOpen(false) }}
      />
    </Paper>
  )
}
